/**
 * History - every conversation you have had, as a page rather than a rail.
 *
 * The drawer's rail is for switching mid-thought; this is for finding something
 * from last Tuesday. It shows what each chat was about, when it happened, which
 * ships it touched and what it produced - the last of which is the interesting
 * one, because it closes the loop from "the assistant proposed a chart" to
 * "that chart is open in Explore".
 *
 * It reads through `assistant-chat-store.ts`, which answers from the account
 * (`ai.conversation`) or from the browser when there is no database - so the
 * page is the same page either way. The list carries no transcripts: what is
 * shown about each chat is summarised where it is stored, and the turns arrive
 * only when one is opened. Search goes to the server in server mode, because
 * the messages are no longer here to filter.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { shipLabel } from '../../utils/ships.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import {
  deleteChat as deleteStoredChat,
  listChats,
  chatsOnServer,
  type AssistantChatSummary,
} from './assistant-chat-store.ts';
import { assistantTechnical, setAssistantTab } from '../../state/assistant-surface.ts';
import { StoreController } from '../../state/controller.ts';
import { assistantMark } from './mark.ts';

/** Chats grouped the way people remember them: today, yesterday, then dates. */
function bucket(iso: string, now = new Date()): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'Earlier';
  const day = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(when)) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'This week';
  if (days < 30) return 'This month';
  return 'Earlier';
}

const ORDER = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'];

/** What came out of it: proposals made, and how many were acted on.

   `applied` is null for a chat read from the account - nothing records an Apply
   click server-side until `ai.proposal_action` is wired - so the clause is left
   off rather than reported as zero. */
function produced(chat: AssistantChatSummary): string | null {
  if (!chat.plots && !chat.exports) return null;
  const parts: string[] = [];
  if (chat.plots) parts.push(`${chat.plots} chart${chat.plots === 1 ? '' : 's'}`);
  if (chat.exports) parts.push(`${chat.exports} export${chat.exports === 1 ? '' : 's'}`);
  const opened = chat.applied ? `, ${chat.applied} opened` : '';
  return `${parts.join(' and ')} proposed${opened}`;
}

@customElement('assistant-history-page')
export class AssistantHistoryPage extends LightElement {
  private store = new StoreController(this, [assistantTechnical]);

  @state() private chats: AssistantChatSummary[] = [];
  @state() private query = '';
  @state() private onServer = false;
  /** Debounces the search: in server mode each keystroke is a query. */
  private searchTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void chatsOnServer().then((server) => {
      this.onServer = server;
    });
    this.reload();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
  }

  /** Re-read on every entry: the drawer may have added a chat since. */
  reload(): void {
    void listChats(this.query).then((chats) => {
      this.chats = chats;
    });
  }

  private onSearch(value: string): void {
    this.query = value;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    // Titles filter locally as you type; the messages behind them are a query,
    // and a keystroke is not a question worth asking the database.
    this.searchTimer = window.setTimeout(() => this.reload(), 220);
  }

  private chatMatches(chat: AssistantChatSummary): boolean {
    const q = this.query.trim().toLowerCase();
    // Server mode has already filtered - including by message, which the
    // browser cannot do once the transcripts are not here.
    if (!q || this.onServer) return true;
    return chat.title.toLowerCase().includes(q);
  }

  private open(chat: AssistantChatSummary): void {
    this.dispatchEvent(
      new CustomEvent('open-chat', { detail: chat.id, bubbles: true, composed: true }),
    );
    setAssistantTab('chat');
  }

  private async deleteChat(chat: AssistantChatSummary, event: Event): Promise<void> {
    event.stopPropagation();
    const ok = await confirmAction({
      title: 'Delete this conversation?',
      message: this.onServer
        ? `"${chat.title}" is deleted from your account, on every machine you use.`
        : `"${chat.title}" is stored in this browser only, so deleting it here deletes it everywhere it exists.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    this.chats = this.chats.filter((c) => c.id !== chat.id);
    await deleteStoredChat(chat.id);
  }

  private renderRow(chat: AssistantChatSummary): TemplateResult {
    const ships = chat.ships;
    const made = produced(chat);
    const usage = chat.usage;
    const turns = chat.questions;
    return html`
      <div class="ah-row" @click=${() => this.open(chat)}>
        <div class="ah-main">
          <span class="ah-title">${chat.title}</span>
          <span class="ah-facts">
            <span>${turns} question${turns === 1 ? '' : 's'}</span>
            ${ships.length
              ? html`<span>${ships.map((s) => shipLabel(s)).join(', ')}</span>`
              : nothing}
            ${made ? html`<span class="ah-made">${made}</span>` : nothing}
            ${assistantTechnical.value && usage.cost
              ? html`<span>$${usage.cost.toFixed(4)}</span>`
              : nothing}
          </span>
        </div>
        <time class="ah-when" datetime=${chat.updatedAt}>
          ${new Date(chat.updatedAt).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </time>
        <button
          class="ah-delete"
          title="Delete this conversation"
          aria-label="Delete ${chat.title}"
          @click=${(e: Event) => void this.deleteChat(chat, e)}
        >
          ×
        </button>
      </div>
    `;
  }

  override render(): TemplateResult {
    const shown = this.chats.filter((c) => this.chatMatches(c));
    const groups = ORDER.map((name) => ({
      name,
      rows: shown.filter((c) => bucket(c.updatedAt) === name),
    })).filter((g) => g.rows.length);

    return html`
      <section class="ah">
        <div class="ah-head">
          <h2 class="ah-heading">Your conversations</h2>
          <input
            id="assistant-history-search"
            class="ah-search"
            type="search"
            placeholder="Search your chats…"
            aria-label="Search your chats"
            .value=${this.query}
            @input=${(e: Event) => this.onSearch((e.target as HTMLInputElement).value)}
          />
        </div>

        <p class="ah-note">
          ${this.onServer
            ? 'Saved to your account, so they are here on any machine you sign in on. Only you can read them.'
            : 'Kept in this browser for 30 days. They do not follow you to another machine.'}
        </p>

        ${groups.length
          ? groups.map(
              (g) => html`
                <div class="ah-group">
                  <h3 class="ah-group-name">${g.name}</h3>
                  <div class="ah-list">${g.rows.map((c) => this.renderRow(c))}</div>
                </div>
              `,
            )
          : html`<div class="ah-empty">
              <span class="ah-empty-mark">${assistantMark(26)}</span>
              <p>
                ${this.chats.length
                  ? 'No conversation matches that.'
                  : 'Nothing here yet. Ask something and it will be waiting for you.'}
              </p>
            </div>`}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'assistant-history-page': AssistantHistoryPage;
  }
}
